import * as React from "react";
import { useEffect } from "react";
import { observer } from "mobx-react-lite";

import { structure } from "@/endpoints/dryp/structure";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { CUSTOM_FILTERS } from "@/config/list";
import { FilterableCheckboxGroupDropdown, type CheckboxGroup } from "@/components/FilterableCheckboxGroupDropdown";
import { ChevronDown } from "lucide-react";
import { StructureTypeIcon } from "@/components/StructureTypeIcon";
import { ListFilters } from "@/types/list";

type StructuresTypesFilterProps = {
	filter: ListFilters;
};

export const StructuresTypesFilter = observer(({ filter }: StructuresTypesFilterProps) => {
	const structureTypesApi = useAsyncStateStore(structure.getAllStructureTypes);

	useEffect(() => {
		structureTypesApi.fetch({ pageNumber: 0, pageSize: 2000 });
	}, []);

	const structureTypes = structureTypesApi?.data?.items || [];
	const loading = structureTypesApi?.loading;

	const selectedTypesIds =
		(filter.getFilter(CUSTOM_FILTERS.structureTypesIds) as (string | number)[] | undefined) ?? [];

	const groups: CheckboxGroup[] = React.useMemo(() => {
		const selectedTypeIdSet = new Set(selectedTypesIds.map(String));

		return [
			{
				name: "Structure types",
				items: structureTypes.map(structureType => ({
					id: structureType.id,
					name: structureType.name,
					checked: selectedTypeIdSet.has(String(structureType.id)),
				})),
			},
		];
	}, [structureTypes, selectedTypesIds]);

	const handleCheckedChange = (typeId: string, checked: boolean) => {
		const normalizedSelectedTypeIds = selectedTypesIds.map(String);
		const normalizedTypeId = String(typeId);
		const nextSelectedTypesIds = checked
			? Array.from(new Set([...normalizedSelectedTypeIds, normalizedTypeId]))
			: normalizedSelectedTypeIds.filter(selectedTypeId => selectedTypeId !== normalizedTypeId);

		filter.setFilter(CUSTOM_FILTERS.structureTypesIds, nextSelectedTypesIds);
	};

	const handleToggleAll = (checked: boolean) => {
		const nextSelectedTypesIds = checked ? structureTypes.map(structureType => String(structureType.id)) : [];
		filter.setFilter(CUSTOM_FILTERS.structureTypesIds, nextSelectedTypesIds);
	};

	return (
		<FilterableCheckboxGroupDropdown
			groups={groups}
			onCheckedChange={handleCheckedChange}
			onToggleAll={handleToggleAll}
			loading={loading}
			buttonVariant="outline"
			buttonClassName="flex items-center gap-2"
			buttonLabel="Types"
			renderItem={item => {
				const id = item.id as Dryp.StructureTypeId;

				return (
					<div className="flex items-center gap-2">
						<StructureTypeIcon typeId={id} showLabel size={18} />
					</div>
				);
			}}
			buttonContent={
				<span className="flex items-center gap-2">
					{selectedTypesIds.length > 0 ? (
						<span className="text-[11px] text-muted-foreground">({selectedTypesIds.length} selected)</span>
					) : (
						<span>All</span>
					)}
					<ChevronDown className="ml-2 h-4 w-4 opacity-50" />
				</span>
			}
			contentAlign="end"
			searchPlaceholder="Search structure types..."
		/>
	);
});
